// Event handlers for pull-to-load functionality

import { isFulltextTabActive, isAtBottomOfPage } from './utils.js';
import { 
    getPullToLoadState, 
    setPullToLoadState,
    getTargetDistance,
    setTargetDistance
} from './state.js';
import { PULL_RESISTANCE, PULL_THRESHOLD, LOAD_THRESHOLD, WHEEL_ACCUMULATION_FACTOR } from './constants.js';
import { startSmoothAnimation } from './animation.js';
import { updatePullIndicator } from './animation.js';
import { loadNextPage } from './loader.js';

/**
 * Handle touch start
 * Only activate if we're at bottom (checked via scroll events)
 * DO NOT preventDefault here - allow normal scrolling
 */
export function handleTouchStart(e) {
    const state = getPullToLoadState();
    
    // Only activate if fulltext tab is active
    if (!isFulltextTabActive()) {
        return;
    }
    
    // Don't activate if on last page
    if (state.currentPage >= state.totalPages) {
        return;
    }
    
    // Check if we're at the bottom using visualViewport-aware function
    // Use stricter threshold (50px) for touch activation
    if (!isAtBottomOfPage(50)) {
        return;
    }
    
    // DO NOT preventDefault here - this would block normal scrolling
    // We'll preventDefault only in touchmove when actually pulling down
    
    setPullToLoadState({
        isActive: true,
        startY: e.touches[0].clientY,
        currentY: e.touches[0].clientY,
        pullDistance: 0
    });
}

/**
 * Handle touch move
 * Process pull gesture when at bottom
 */
export function handleTouchMove(e) {
    const state = getPullToLoadState();
    
    // Only process if fulltext tab is active
    if (!isFulltextTabActive()) {
        if (state.isActive) {
            setPullToLoadState({
                isActive: false,
                pullDistance: 0
            });
            updatePullIndicator(0);
        }
        return;
    }
    
    // First check if we should activate (user might have scrolled to bottom during touch)
    if (!state.isActive) {
        // Check if we're at bottom and should activate
        if (isAtBottomOfPage(100) && state.currentPage < state.totalPages) {
            // Activate if at bottom and not last page
            setPullToLoadState({
                isActive: true,
                startY: e.touches[0].clientY,
                currentY: e.touches[0].clientY,
                pullDistance: 0
            });
        } else {
            return;
        }
    }
    
    // Check if we're still at the bottom (with tolerance for bounce effect)
    // Use larger threshold (200px) to account for iOS bounce/rubber-band effect and browser UI changes
    if (!isAtBottomOfPage(200)) {
        setPullToLoadState({
            isActive: false,
            pullDistance: 0
        });
        updatePullIndicator(0);
        return;
    }
    
    const currentY = e.touches[0].clientY;
    const currentState = getPullToLoadState();
    const deltaY = currentState.startY - currentY;
    
    // Only allow pulling down (positive deltaY means finger moving down, which is pull-to-load)
    if (deltaY < 0) {
        // User is scrolling up, reset and allow normal scrolling
        setPullToLoadState({
            isActive: false,
            pullDistance: 0
        });
        updatePullIndicator(0);
        return;
    }
    
    // Apply resistance
    const pullDistance = deltaY * PULL_RESISTANCE;
    setPullToLoadState({
        currentY: currentY,
        pullDistance: pullDistance
    });
    
    // Update indicator
    updatePullIndicator(pullDistance);
    
    // Only prevent default when pulling down significantly (at least 15px)
    // This prevents browser's pull-to-refresh while allowing normal scrolling
    // Higher threshold prevents blocking normal scroll gestures
    if (pullDistance > 15) {
        e.preventDefault();
    }
}

/**
 * Handle touch end
 */
export function handleTouchEnd(e) {
    const state = getPullToLoadState();
    
    if (!state.isActive) return;
    
    setPullToLoadState({ isActive: false });
    
    // Check if we should load next page
    if (state.pullDistance >= LOAD_THRESHOLD && !state.isLoading) {
        loadNextPage();
    } else {
        // Reset indicator
        updatePullIndicator(0, true);
    }
}

/**
 * Handle scroll event
 * This is the primary activation method for mobile devices
 */
export function handleScroll() {
    const state = getPullToLoadState();
    
    // Only process if fulltext tab is active
    if (!isFulltextTabActive()) {
        if (state.pullDistance > 0) {
            setPullToLoadState({ pullDistance: 0 });
            setTargetDistance(0);
            startSmoothAnimation();
        }
        return;
    }
    
    // Don't activate if on last page
    if (state.currentPage >= state.totalPages) {
        if (state.pullDistance > 0) {
            setPullToLoadState({ pullDistance: 0 });
            setTargetDistance(0);
            startSmoothAnimation();
        }
        return;
    }
    
    // Use visualViewport-aware function for accurate bottom detection
    // Use larger threshold (100px) for scroll events to activate earlier
    const isAtBottom = isAtBottomOfPage(100);
    
    // On mobile: activate pull-to-load when scrolling reaches bottom
    // This works even when browser UI is hidden
    if (isAtBottom && !state.isActive && !state.isLoading) {
        // User scrolled to bottom - prepare for pull-to-load
        // Don't activate yet, wait for touch gesture
        setPullToLoadState({ pullDistance: 0 });
        setTargetDistance(0);
    }
    
    // Reset pull distance if not at bottom (gentle decay)
    if (!isAtBottom && state.pullDistance > 0) {
        const newDistance = Math.max(0, state.pullDistance - 1.5); // Very slow decay (2x slower than 3)
        setPullToLoadState({ pullDistance: newDistance });
        setTargetDistance(newDistance);
        startSmoothAnimation();
    }
}

/**
 * Handle wheel (mouse scroll)
 */
export function handleWheel(e) {
    const state = getPullToLoadState();
    
    // Only process if fulltext tab is active
    if (!isFulltextTabActive()) {
        if (state.pullDistance > 0) {
            // Reset pull distance if not on fulltext tab
            const newDistance = Math.max(0, state.pullDistance - Math.abs(e.deltaY) * 0.5);
            setPullToLoadState({ pullDistance: newDistance });
            setTargetDistance(newDistance);
            startSmoothAnimation();
        }
        return;
    }
    
    // Don't activate if on last page
    if (state.currentPage >= state.totalPages) {
        if (state.pullDistance > 0) {
            // Reset pull distance if on last page
            const newDistance = Math.max(0, state.pullDistance - Math.abs(e.deltaY) * 0.5);
            setPullToLoadState({ pullDistance: newDistance });
            setTargetDistance(newDistance);
            startSmoothAnimation();
        }
        return;
    }
    
    // Use visualViewport-aware function for accurate bottom detection
    // Use larger threshold (150px) for desktop to account for scroll momentum
    const isAtBottom = isAtBottomOfPage(150);
    const isScrollingDown = e.deltaY > 0;
    
    if (!isAtBottom || !isScrollingDown) {
        if (state.pullDistance > 0) {
            // Gentle decay when not at bottom (slower, smoother)
            const newDistance = Math.max(0, state.pullDistance - Math.abs(e.deltaY) * 0.15);
            setPullToLoadState({ pullDistance: newDistance });
            setTargetDistance(newDistance);
            startSmoothAnimation();
        }
        return;
    }
    
    // Prevent default scroll when pulling (only if already pulling significantly)
    if (state.pullDistance > PULL_THRESHOLD * 0.5) {
        e.preventDefault();
    }
    
    // Accumulate pull distance - use increased factor for desktop (wheel events need more accumulation)
    // WHEEL_ACCUMULATION_FACTOR (0.08) is too slow, use 3x for desktop responsiveness
    // This makes wheel scrolling more responsive on desktop
    const newDistance = state.pullDistance + Math.abs(e.deltaY) * (WHEEL_ACCUMULATION_FACTOR * 3);
    setPullToLoadState({ pullDistance: newDistance });
    setTargetDistance(newDistance);
    
    // Start smooth animation loop (gentle interpolation)
    startSmoothAnimation();
    
    // Auto-load when threshold reached
    if (newDistance >= LOAD_THRESHOLD && !state.isLoading) {
        loadNextPage();
    }
}

